import React, { useMemo, useState } from "react";
import { AddCircle, Tornado } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { PXDX_RFG } from "../Constants/PolicyTranslationConstants";
import PolicyRulesDataGrid from "../DataGrids/PolicyRulesDataGrid";
import ProcedureCodeDialog from "../../PolicyTranslation/Dialogs/ProcedureCodeDialog";
import ActionsFragment from "../Fragments/ActionsFragment";
import UseCasesActionsFragment from "../Fragments/UseCasesActionsFragment";
import StartDateFragment from "../Fragments/StartDateFragment";
import DuplicateRowFragment from "../Fragments/DuplicateRowFragment";
import { useGridApiRef } from "@mui/x-data-grid";

import { useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import RadioGenderFragment from "../Fragments/RadioGenderFragment";
import GenderAgeMeetCC from "../Fragments/GenderAgeMeetCC";
import { getAgeRange, getLobName } from "../Utils/TranslationUtils";
import { renderError, renderCellClass } from "../Validation/ValidationHelper";
import { showVarianceIcon } from "utils/shared";
import StyledTornadoIcon from "components/TornadoIcon";
const AddProcedureCode = styled("a")(({ theme }) => {
  return {
    textTransform: "uppercase" /* Make text uppercase */,
    color: "blue" /* Standard hyperlink color */,
    pointerEvents: "none" /* Make the link not clickable */,
    cursor: "default" /* Change cursor to default */,
  };
});

const PxDxTable = ({
  gridHeight,
  setLoading,
  rows,
  updateRulesAfterEdit,
  availableProcedureCodes,
  chips,
  editable,
  setSelectedCC,
  addNewRows,
  duplicateRow,
  selectedCC,
  selectedVariance,
  setSelectedVariance,
  handleDelete,
  policyData,
  dxLimitationValues,
  pageComponent,
  handleRemoveInvalidRow,
  endExistingRule,
}) => {
  const [isPxDialogOpen, setIsPxDialogOpen] = useState(false);
  const [openDxDialog, setOpenDxDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const apiRef = useGridApiRef();
  const { state } = useLocation();
  const selectedHealthPlan = state?.policyState?.healthPlanName;

  const handleAddProcedureCode = () => {
    setIsPxDialogOpen(true);
  };

  const handleAddRowToGrid = (checkedItems) => {
    addNewRows(checkedItems, PXDX_RFG);
    setOpenDxDialog(false);
  };

  const columns = [
    {
      display: "flex",
      field: "CoverageCriteriaList",
      headerName: "CC#",
      width: 100,
      editable: editable,
      valueGetter: (value, row) => {
        return row.RuleFacts.CoverageCriteriaList?.trim() || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.CoverageCriteriaList = value ? value?.trim() : "";
        return newRow;
      },
      cellClassName: (params) => renderCellClass(params),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);

        return (
          <Box display="flex" alignItems="center">
            <Typography sx={{ mr: 1 }}>
              {error
                ? error
                : Array.isArray(params.value)
                  ? params.value.join(",")
                  : params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      display: "flex",
      field: "procedureCode",
      headerName: "Procedure Code",
      width: 150,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <DuplicateRowFragment
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            value={params.row.RuleFacts.ProcedureCode}
            duplicateRow={duplicateRow}
          />
          {/* This is used to identify variances in new rules and rules that
              have completed the sheet generation process */}
          {editable &&
            showVarianceIcon(params.row.RuleHeader, selectedHealthPlan) && (
              <StyledTornadoIcon />
            )}
        </Box>
      ),
      valueGetter: (value, row) => row.RuleFacts.ProcedureCode,
    },
    {
      display: "flex",
      field: "dxLimitation",
      headerName: "Dx Limitation",
      flex: 1.1,
      width: 200,
      editable: editable,
      type: "singleSelect",
      valueOptions: dxLimitationValues.map((option) => option.displayValueText),

      renderCell: (params) => {
        const error = renderError(params.field, params.row);
        if (error) {
          return error;
        }
        return params.value;
      },

      valueGetter: (value, row) => {
        const key = row.RuleFacts?.DXDisplayValueKey;
        if (!key) {
          return "";
        }
        const foundOption = dxLimitationValues.find(
          (opt) => String(opt.dxLimitationKey) === String(key)
        );
        return foundOption ? foundOption.displayValueText : "";
      },

      valueSetter: (value, row) => {
        const foundOption = dxLimitationValues.find(
          (opt) => opt.displayValueText === value
        );
        const newRow = structuredClone(row);
        if (foundOption) {
          newRow.RuleFacts.DXDisplayValueKey = String(
            foundOption.dxLimitationKey
          );
          newRow.RuleFacts.DXLimitation = foundOption.apeaValueText;
        } else {
          newRow.RuleFacts.DXDisplayValueKey = "";
          newRow.RuleFacts.DXLimitation = "";
        }
        return newRow;
      },

      cellClassName: (params) => renderCellClass(params),
    },
    {
      display: "flex",
      field: "coveredGender",
      headerName: "Gender",
      minWidth: 190,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return (
          <RadioGenderFragment
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
          />
        );
      },

      renderEditCell: (params) => {
        return (
          <RadioGenderFragment
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
          />
        );
      },
    },
    {
      display: "flex",
      field: "genderAgeMeetsCC",
      headerName: "Gender & Age Meets CC?",
      minWidth: 260,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      renderEditCell: (params) => (
        <GenderAgeMeetCC
          editable={
            editable &&
            pageComponent === "PolicyTranslation" &&
            params.row.RuleHeader.EditRule !== "EDIT"
          }
          params={params}
          updateRulesAfterEdit={updateRulesAfterEdit}
        />
      ),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return (
          <GenderAgeMeetCC
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
          />
        );
      },
    },
    {
      display: "flex",
      field: "ageLow",
      headerName: "Age Low",
      flex: 0.4,
      align: "center",
      headerAlign: "center",
      cellClassName: (params) => renderCellClass(params),

      minWidth: 140,
      editable: editable,
      valueGetter: (value, row) => {
        return row.RuleFacts.ageLow?.trim() || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.DisplayAgeRange = `${value},${row.RuleFacts.ageHigh}`;
        newRow.RuleFacts.ageLow = value?.trim() || "";
        return newRow;
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return row.RuleFacts.ageLow?.trim() || "";
      },
    },
    {
      display: "flex",
      field: "ageHigh",
      headerName: "Age High",
      flex: 0.4,
      minWidth: 140,
      align: "center",
      headerAlign: "center",
      editable: editable,
      cellClassName: (params) => renderCellClass(params),

      valueGetter: (value, row) => {
        return row.RuleFacts.ageHigh?.trim() || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.DisplayAgeRange = `${row.RuleFacts.ageLow},${value}`;
        newRow.RuleFacts.ageHigh = value?.trim() || "";
        return newRow;
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return row.RuleFacts.ageHigh?.trim() || "";
      },
    },
    {
      display: "flex",
      field: "startDate",
      headerName: "Date & LOB Configuration",
      flex: 1.1,
      minWidth: 220,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);

        if (error) {
          return error;
        }
        return (
          <StartDateFragment
            updateRulesAfterEdit={updateRulesAfterEdit}
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            policyData={policyData}
            variant={
              editable &&
              (["NEW", "VARIANCE"].includes(params.row.RuleHeader?.EditRule) ||
                params.row.RuleHeader?.RuleVersion?.includes("-")) &&
              selectedHealthPlan !== "AHCS"
            }
            endExistingRule={endExistingRule}
          />
        );
      },
      renderEditCell: (params) => (
        <StartDateFragment
          updateRulesAfterEdit={updateRulesAfterEdit}
          handleRemoveInvalidRow={handleRemoveInvalidRow}
          editable={editable && pageComponent === "PolicyTranslation"}
          params={params}
          policyData={policyData}
          variant={
            editable &&
            (["NEW", "VARIANCE"].includes(params.row.RuleHeader?.EditRule) ||
              params.row.RuleHeader?.RuleVersion?.includes("-")) &&
            selectedHealthPlan !== "AHCS"
          }
          endExistingRule={endExistingRule}
        />
      ),
    }, //new column goes here.
    {
      display: "flex",
      field: "LobDisplayName",
      headerName: "LOB",
      minWidth: 150,
      valueGetter: (value, row) => {
        return getLobName(row.RuleFacts.LobConfigKey, policyData);
      },
      cellClassName: (params) => renderCellClass(params),

      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return params.value || "";
      },
    },
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => {
        return pageComponent === "PolicyTranslation" ? (
          <ActionsFragment
            handleDelete={handleDelete}
            editable={editable}
            params={params}
            disabled
          />
        ) : (
          <UseCasesActionsFragment
            editable={editable && pageComponent === "UseCases"}
            params={params}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
          />
        );
      },
    },
  ];

  const sanitizedRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return rows.map((row) => {
      const sanitizedRow = { ...row };
      const ageRange = getAgeRange(
        row?.RuleFacts?.DisplayAgeRange,
        row?.RuleFacts?.InvalidAgeRange,
        row?.RuleFacts?.DisplayAgeGenderRule
      );
      sanitizedRow.RuleFacts = { ...row.RuleFacts };

      sanitizedRow.RuleFacts.ageHigh = ageRange[1];
      sanitizedRow.RuleFacts.ageLow = ageRange[0];
      return sanitizedRow;
    });
  }, [rows]);

  const columnGroupingModel = [
    {
      groupId: "Age Range",
      children: [{ field: "ageLow" }, { field: "ageHigh" }],
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {pageComponent !== "UseCases" && (
        <Box>
          <IconButton
            disabled={!editable}
            onClick={handleAddProcedureCode}
            color="primary"
            aria-label="add new row"
          >
            <AddCircle />
          </IconButton>
          <AddProcedureCode>Add Procedure Code</AddProcedureCode>
        </Box>
      )}
      <PolicyRulesDataGrid
        gridHeight={gridHeight}
        setLoading={setLoading}
        apiRef={apiRef}
        editable={editable && pageComponent === "PolicyTranslation"}
        rows={sanitizedRows}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "procedureCode", sort: "asc" }],
          },
        }}
        columnGroupingModel={columnGroupingModel}
        processRowUpdate={(newRow, oldRow) =>
          updateRulesAfterEdit(newRow, oldRow)
        }
      />
      <ProcedureCodeDialog
        availableProcedureCodes={availableProcedureCodes}
        selectedCC={selectedCC}
        setSelectedCC={setSelectedCC}
        selectedVariance={selectedVariance}
        setSelectedVariance={setSelectedVariance}
        chips={chips}
        open={isPxDialogOpen}
        onClose={() => setIsPxDialogOpen(false)}
        onSave={handleAddRowToGrid}
      />
    </div>
  );
};

export default PxDxTable;
